/**
 * Proposal Type Definitions
 * For generating custom proposals for high-ticket leads
 */

import { Timestamp } from 'firebase/firestore';
import { PricingTier } from './lead';

export type ProposalStatus = 'draft' | 'sent' | 'viewed' | 'accepted' | 'rejected' | 'expired';

export type ProposalTemplate = 'standard' | 'premium' | 'enterprise' | 'custom';

export type ProposalLanguage = 'en' | 'fr' | 'es';

export interface ProposalVideo {
  url: string;
  provider: 'youtube' | 'vimeo' | 'custom';
  thumbnailUrl?: string;
  title?: string;
  duration?: number; // seconds
  viewCount?: number;
  lastViewedAt?: Timestamp;
}

export interface ProposalNotificationSettings {
  smsEnabled: boolean;
  phoneNumber?: string;
  notifyOnSent?: boolean;
  notifyOnViewed?: boolean;
  notifyOnSigned?: boolean;
  notifyOnExpiring?: boolean; // 7 days before expiry
}

export interface ProposalPricing {
  doorCount: number;
  pricingTier: PricingTier;
  basePrice: number;
  discountPercent: number;  // Changed from optional to required - always provide a value (0 if no discount)
  discountAmount: number;   // Changed from optional to required - always provide a value (0 if no discount)
  finalPrice: number;
  paymentSchedule: 'annual' | 'semi-annual' | 'quarterly';
  contractTerm: number; // in months (12, 24, 36)
}

export interface ROICalculation {
  currentMonthlyCost: number; // Current solution cost
  proposedMonthlyCost: number; // DossiLoc monthly cost
  monthlySavings: number;
  annualSavings: number;
  timeCurrentlySpent: number; // Hours per month
  timeWithDossiLoc: number; // Hours per month
  timeSaved: number; // Hours per month
  hourlyRate: number; // Client's hourly rate
  timeSavingsValue: number; // Monthly value of time saved
  totalMonthlyValue: number; // Savings + time value
  totalAnnualValue: number;
  roiPercentage: number;
  paybackPeriod: number; // Months to break even
}

export interface ProposalSection {
  id: string;
  title: string;
  content: string;
  order: number;
  editable: boolean;
  video?: ProposalVideo; // Optional video embed
}

export interface ProposalFeature {
  name: string;
  description: string;
  included: boolean;
  highlighted?: boolean;
}

export interface ProposalMetrics {
  viewCount: number;
  firstViewedAt?: Timestamp;
  lastViewedAt?: Timestamp;
  timeSpentViewing?: number; // Total seconds
  downloadCount: number;
  sharedCount: number;
}

export interface Proposal {
  id: string;
  leadId: string;
  leadName: string;
  leadEmail: string;
  leadCompany?: string;
  status: ProposalStatus;
  template: ProposalTemplate;
  
  // P3 Enhancements
  language: ProposalLanguage; // Multi-language support
  notificationSettings?: ProposalNotificationSettings; // SMS notifications
  interactivePricingEnabled?: boolean; // Recipient can adjust pricing
  
  // Proposal content
  title: string;
  sections: ProposalSection[];
  features: ProposalFeature[];
  
  // Pricing & ROI
  pricing: ProposalPricing;
  roiCalculation: ROICalculation;
  
  // Pain points from lead
  painPointsAddressed: string[];
  
  // Validity (converted to Date when fetched from Firestore)
  validUntil: Date | Timestamp;
  
  // Tracking
  metrics: ProposalMetrics;
  
  // Signature
  signatureRequired: boolean;
  signedAt?: Date | Timestamp;
  signedBy?: string;
  signatureData?: string; // Base64 signature image or DocuSign envelope ID
  
  // Files
  pdfUrl?: string;
  pdfGeneratedAt?: Date | Timestamp;
  
  // Metadata
  createdAt: Date | Timestamp;
  createdBy: string;
  createdByName: string;
  updatedAt: Date | Timestamp;
  sentAt?: Date | Timestamp;
  acceptedAt?: Date | Timestamp;
  rejectedAt?: Date | Timestamp;
  rejectionReason?: string;
  notes?: string;
}

export interface ProposalCreateData {
  leadId: string;
  template?: ProposalTemplate;
  pricing: ProposalPricing;
  roiCalculation: ROICalculation;
  customSections?: Partial<ProposalSection>[];
  validityDays?: number; // Default 30 days
  signatureRequired?: boolean;
  notes?: string;
}

export interface ProposalUpdateData {
  title?: string;
  sections?: ProposalSection[];
  features?: ProposalFeature[];
  pricing?: ProposalPricing;
  roiCalculation?: ROICalculation;
  validUntil?: Timestamp;
  notes?: string;
}

export interface ProposalFilters {
  leadId?: string;
  status?: ProposalStatus;
  template?: ProposalTemplate;
  createdBy?: string;
  sentAfter?: Timestamp;
  sentBefore?: Timestamp;
  search?: string;
  sortBy?: 'createdAt' | 'sentAt' | 'finalPrice' | 'leadName';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ProposalStats {
  total: number;
  byStatus: Record<ProposalStatus, number>;
  byTemplate: Record<ProposalTemplate, number>;
  totalValue: number;
  avgProposalValue: number;
  avgTimeToAccept: number; // Days from sent to accepted
  conversionRate: number; // Accepted / Sent
  avgViewCount: number;
  avgTimeSpentViewing: number; // Seconds
}

// Helper functions

export function getProposalStatusDisplay(status: ProposalStatus): string {
  const displays: Record<ProposalStatus, string> = {
    draft: 'Draft',
    sent: 'Sent',
    viewed: 'Viewed',
    accepted: 'Accepted',
    rejected: 'Rejected',
    expired: 'Expired',
  };
  return displays[status];
}

export function getProposalStatusColor(status: ProposalStatus): string {
  const colors: Record<ProposalStatus, string> = {
    draft: 'gray',
    sent: 'blue',
    viewed: 'purple',
    accepted: 'green',
    rejected: 'red',
    expired: 'orange',
  };
  return colors[status];
}

export function calculateROI(
  currentMonthlyCost: number,
  proposedMonthlyCost: number,
  timeCurrentlySpent: number,
  timeWithDossiLoc: number,
  hourlyRate: number
): ROICalculation {
  const monthlySavings = currentMonthlyCost - proposedMonthlyCost;
  const annualSavings = monthlySavings * 12;
  
  const timeSaved = timeCurrentlySpent - timeWithDossiLoc;
  const timeSavingsValue = timeSaved * hourlyRate;
  
  const totalMonthlyValue = monthlySavings + timeSavingsValue;
  const totalAnnualValue = totalMonthlyValue * 12;
  
  const roiPercentage = proposedMonthlyCost > 0
    ? (totalMonthlyValue / proposedMonthlyCost) * 100
    : 0;
  
  const paybackPeriod = totalMonthlyValue > 0
    ? Math.ceil(proposedMonthlyCost / totalMonthlyValue)
    : 0;
  
  return {
    currentMonthlyCost,
    proposedMonthlyCost,
    monthlySavings,
    annualSavings,
    timeCurrentlySpent,
    timeWithDossiLoc,
    timeSaved,
    hourlyRate,
    timeSavingsValue,
    totalMonthlyValue,
    totalAnnualValue,
    roiPercentage,
    paybackPeriod,
  };
}

export function getDefaultSections(template: ProposalTemplate): ProposalSection[] {
  const common: ProposalSection[] = [
    {
      id: 'executive-summary',
      title: 'Executive Summary',
      content: '',
      order: 1,
      editable: true,
    },
    {
      id: 'problem',
      title: 'Current Challenges',
      content: '',
      order: 2,
      editable: true,
    },
    {
      id: 'solution',
      title: 'Our Solution',
      content: '',
      order: 3,
      editable: true,
    },
    {
      id: 'roi',
      title: 'Return on Investment',
      content: '', // Auto-generated from ROI calculation
      order: 4,
      editable: false,
    },
    {
      id: 'features',
      title: 'Features & Benefits',
      content: '',
      order: 5,
      editable: true,
    },
    {
      id: 'pricing',
      title: 'Investment',
      content: '', // Auto-generated from pricing
      order: 6,
      editable: false,
    },
    {
      id: 'implementation',
      title: 'Implementation Timeline',
      content: '',
      order: 7,
      editable: true,
    },
    {
      id: 'next-steps',
      title: 'Next Steps',
      content: '',
      order: 8,
      editable: true,
    },
  ];

  if (template === 'premium' || template === 'enterprise') {
    common.push({
      id: 'case-studies',
      title: 'Success Stories',
      content: '',
      order: 5.5,
      editable: true,
    });
  }

  if (template === 'enterprise') {
    common.push({
      id: 'security',
      title: 'Security & Compliance',
      content: '',
      order: 7.5,
      editable: true,
    });
  }

  return common.sort((a, b) => a.order - b.order);
}

export function getDefaultFeatures(pricingTier: PricingTier): ProposalFeature[] {
  const allFeatures: ProposalFeature[] = [
    {
      name: 'Automated Rent Collection',
      description: 'Direct deposit via email money transfer',
      included: true,
      highlighted: true,
    },
    {
      name: 'Gmail Integration',
      description: 'Seamless integration with your Gmail account',
      included: true,
      highlighted: true,
    },
    {
      name: 'Tenant Portal',
      description: 'Self-service portal for tenants',
      included: true,
    },
    {
      name: 'Payment Tracking',
      description: 'Real-time payment status and history',
      included: true,
    },
    {
      name: 'Late Payment Reminders',
      description: 'Automated reminders for overdue rent',
      included: true,
    },
    {
      name: 'Financial Reports',
      description: 'Monthly income and expense reports',
      included: true,
    },
    {
      name: 'Multi-Property Management',
      description: 'Manage multiple properties from one dashboard',
      included: true,
    },
    {
      name: 'Expense Tracking',
      description: 'Track and categorize property expenses',
      included: true,
    },
    {
      name: 'White-Glove Onboarding',
      description: 'Done-for-you setup and training',
      included: pricingTier !== '10-20',
      highlighted: pricingTier !== '10-20',
    },
    {
      name: 'Priority Support',
      description: '24-hour response time',
      included: pricingTier === '100-250' || pricingTier === '250+',
      highlighted: pricingTier === '100-250' || pricingTier === '250+',
    },
    {
      name: 'Custom Workflows',
      description: 'Tailored workflows for your processes',
      included: pricingTier === '250+',
      highlighted: pricingTier === '250+',
    },
    {
      name: 'API Access',
      description: 'Integration with your existing systems',
      included: pricingTier === '250+',
      highlighted: pricingTier === '250+',
    },
  ];

  return allFeatures;
}
